import React, { useState, useEffect, useCallback } from "react";
import {
  Spinner,
  Alert,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import {
  generateApiKey,
  listApiKeys,
  revokeApiKey,
  deleteApiKey,
} from "../../ApiCalls/apiKeys";
import "./styles/apiKeysTab.scss";

function ApiKeysTab({ mantisBusinessId, token }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [generatingKey, setGeneratingKey] = useState(false);
  const [newGeneratedKey, setNewGeneratedKey] = useState(null);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [deletingKeyId, setDeletingKeyId] = useState(null);
  const [revokingKeyId, setRevokingKeyId] = useState(null);

  const fetchApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setKeys(await listApiKeys(token, mantisBusinessId));
    } catch (err) {
      setError(err.message || "Failed to load API keys");
    } finally {
      setLoading(false);
    }
  }, [token, mantisBusinessId]);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleGenerateKey = async () => {
    if (!newKeyName.trim()) {
      setError("Please enter a key name");
      return;
    }

    try {
      setGeneratingKey(true);
      setError("");
      const result = await generateApiKey(token, mantisBusinessId, newKeyName);
      setNewGeneratedKey(result);
      setShowGenerateModal(false);
      setShowNewKeyModal(true);
      setNewKeyName("");
      await fetchApiKeys();
    } catch (err) {
      setError(err.message || "Failed to generate API key");
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm("Are you sure you want to revoke this API key?")) {
      return;
    }

    try {
      setRevokingKeyId(keyId);
      setError("");
      await revokeApiKey(token, keyId, mantisBusinessId);
      setSuccess("API key revoked successfully");
      setTimeout(() => setSuccess(""), 3000);
      await fetchApiKeys();
    } catch (err) {
      setError(err.message || "Failed to revoke API key");
    } finally {
      setRevokingKeyId(null);
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm("Are you sure you want to delete this API key?")) {
      return;
    }

    try {
      setDeletingKeyId(keyId);
      setError("");
      await deleteApiKey(token, keyId, mantisBusinessId);
      setSuccess("API key deleted successfully");
      setTimeout(() => setSuccess(""), 3000);
      await fetchApiKeys();
    } catch (err) {
      setError(err.message || "Failed to delete API key");
    } finally {
      setDeletingKeyId(null);
    }
  };

  const handleCopyKey = () => {
    if (newGeneratedKey?.key) {
      navigator.clipboard.writeText(newGeneratedKey.key);
      setSuccess("API key copied to clipboard!");
      setTimeout(() => setSuccess(""), 2000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return "N/A";
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="badge bg-success">Active</span>
    ) : (
      <span className="badge bg-danger">Revoked</span>
    );
  };

  if (loading) {
    return (
      <div className="api-keys-tab">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="api-keys-tab">
      {error && <Alert color="danger">{error}</Alert>}
      {success && <Alert color="success">{success}</Alert>}

      <div className="keys-header">
        <h5>🔐 API Keys Management</h5>
        <Button
          color="primary"
          onClick={() => setShowGenerateModal(true)}
        >
          <i className="fas fa-plus" /> Generate New Key
        </Button>
      </div>

      {keys.length === 0 ? (
        <Alert color="info">
          No API keys yet. Click "Generate New Key" to create your first API key.
        </Alert>
      ) : (
        <div className="keys-table-container">
          <Table responsive hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Prefix</th>
                <th>Status</th>
                <th>Last Used</th>
                <th>Expires</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key._id} className={!key.isActive ? "revoked-key" : ""}>
                  <td>
                    <strong>{key.name}</strong>
                  </td>
                  <td>
                    <code>{key.keyPrefix}***</code>
                  </td>
                  <td>{getStatusBadge(key.isActive)}</td>
                  <td>{formatDate(key.lastUsedAt)}</td>
                  <td>{formatDate(key.expiresAt)}</td>
                  <td>{formatDate(key.dateCreated)}</td>
                  <td>
                    <div className="action-buttons">
                      {key.isActive && (
                        <Button
                          size="sm"
                          color="warning"
                          onClick={() => handleRevokeKey(key._id)}
                          disabled={revokingKeyId === key._id}
                        >
                          {revokingKeyId === key._id ? (
                            <Spinner size="sm" />
                          ) : (
                            <>
                              <i className="fas fa-ban" /> Revoke
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        color="danger"
                        onClick={() => handleDeleteKey(key._id)}
                        disabled={deletingKeyId === key._id}
                      >
                        {deletingKeyId === key._id ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            <i className="fas fa-trash" /> Delete
                          </>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Generate Key Modal */}
      <Modal isOpen={showGenerateModal} toggle={() => setShowGenerateModal(false)}>
        <ModalHeader toggle={() => setShowGenerateModal(false)}>
          Generate New API Key
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="keyName">Key Name</Label>
              <Input
                type="text"
                name="keyName"
                id="keyName"
                placeholder="e.g., Production Integration"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
              />
              <small className="text-muted">
                Choose a descriptive name to identify this key
              </small>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowGenerateModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleGenerateKey}
            disabled={generatingKey || !newKeyName.trim()}
          >
            {generatingKey ? (
              <>
                <Spinner size="sm" /> Generating...
              </>
            ) : (
              "Generate"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* New Key Display Modal */}
      <Modal isOpen={showNewKeyModal} toggle={() => setShowNewKeyModal(false)}>
        <ModalHeader toggle={() => setShowNewKeyModal(false)}>
          🔐 Your New API Key
        </ModalHeader>
        <ModalBody>
          <Alert color="warning">
            <strong>⚠️ Important:</strong> This is the only time you'll see this key.
            Copy and store it in a safe place.
          </Alert>
          {newGeneratedKey && (
            <div className="new-key-display">
              <div className="key-container">
                <code className="key-value">{newGeneratedKey.key}</code>
                <Button
                  size="sm"
                  color="info"
                  onClick={handleCopyKey}
                  className="copy-btn"
                >
                  <i className="fas fa-copy" /> Copy
                </Button>
              </div>
              <small className="text-muted d-block mt-2">
                Key Name: <strong>{newGeneratedKey.name}</strong>
              </small>
              <small className="text-muted d-block">
                Prefix: <strong>{newGeneratedKey.keyPrefix}</strong>
              </small>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => setShowNewKeyModal(false)}
          >
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default ApiKeysTab;
